/**
 * Reusable sample selection UI for shipping/transfer.
 * Encapsulates add-by-label, lookup dialog, table display, and callbacks.
 * Used by ShippingDetails (edit) and AddShippingDialog (create).
 */

'use client';

import { FC, useState, useRef } from 'react';
import { ShippingSamplesTable } from './ShippingSamplesTable';
import { ApiException, SampleWithLabelDto, SampleDto, ShippingDto } from '@/api/ApiClient';
import type { ShippingDialogLaunchFrom } from '@/stores/dialogStore';
import { useBackend } from '@/hooks/useBackend';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { SampleSelectionDialog } from '@/components/dialog/SampleSelectionDialog';
import { useSampleFilterStore, defaultFilterCriteria } from '@/stores/sampleFilterStore';
import type { SampleSearchCriteria } from '@/api/ApiClient';

export interface ShippingSampleSelectorProps {
  /** Project label for sample lookups (backend.project.getSample) */
  projectLabel: string;
  /** Selected samples (controlled) */
  samples: SampleWithLabelDto[];
  /** Called when samples change */
  onSamplesChange: (samples: SampleWithLabelDto[]) => void;
  /** Whether this is a local transfer (hides label type column) */
  isLocalTransfer?: boolean;
  /** Whether the selector is read-only */
  isReadOnly?: boolean;
  /** Whether the shipping is pending (enables edit/remove) */
  isPending?: boolean;
  /** Whether the shipping is submitted (disables add/remove) */
  isSubmitted?: boolean;
  /** Available label types for the dropdown */
  availableLabelTypes: string[];
  /**
   * When set from Add Shipping: `projectTree` seeds the sample dialog project filter to `projectLabel`;
   * `shippingsPage` leaves project unset so users can search any accessible sample (filters can still be changed).
   */
  shippingDialogLaunchFrom?: ShippingDialogLaunchFrom;
  /** Whether samples are required for submit validation */
  requiredToSubmit?: boolean;
  /** Error message to display (e.g. validation) */
  errorMessage?: string;
  /** Called when samples are modified (e.g. to clear validation error) */
  onSamplesModified?: () => void;
}

export const ShippingSampleSelector: FC<ShippingSampleSelectorProps> = ({
  projectLabel,
  samples,
  onSamplesChange,
  isLocalTransfer = false,
  isReadOnly = false,
  isPending = true,
  isSubmitted = false,
  availableLabelTypes,
  shippingDialogLaunchFrom,
  requiredToSubmit = false,
  errorMessage,
  onSamplesModified,
}) => {
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();
  const { setFilters } = useSampleFilterStore();
  const previousFiltersRef = useRef<SampleSearchCriteria | null>(null);

  const [sampleLabelToAdd, setSampleLabelToAdd] = useState('');
  const [showSampleDialog, setShowSampleDialog] = useState(false);

  const handleOpenSampleDialog = () => {
    previousFiltersRef.current = useSampleFilterStore.getState().filters;
    const seedProject = shippingDialogLaunchFrom === 'shippingsPage' ? '' : projectLabel.trim();
    setFilters({
      ...defaultFilterCriteria,
      projectId: seedProject,
      includeSplitPieces: true,
    });
    setShowSampleDialog(true);
  };

  const handleCloseSampleDialog = () => {
    setShowSampleDialog(false);
    if (previousFiltersRef.current) {
      setFilters(previousFiltersRef.current);
      previousFiltersRef.current = null;
    }
  };

  const handleAddSample = (sample: SampleDto) => {
    if (samples.some((s) => s.sampleId === sample.id)) {
      showSnackbar('Sample is already in this shipping', 'warning');
      return;
    }
    const newSample: SampleWithLabelDto = {
      sampleId: sample.id!,
      sample: sample,
      labelType: undefined,
    };
    onSamplesChange([...samples, newSample]);
    onSamplesModified?.();
  };

  const handleAddSampleByLabel = async () => {
    if (!sampleLabelToAdd.trim()) return;
    try {
      const sample = await backend.sample.get(sampleLabelToAdd.trim());
      handleAddSample(sample);
      setSampleLabelToAdd('');
    } catch (error) {
      if (ApiException.isApiException(error)) {
        if (error.status === 404) {
          log.warn('[ShippingSampleSelector] No sample found for label', sampleLabelToAdd.trim());
          showSnackbar('Could not find a sample with that label.', 'warning');
        } else if (error.status === 403) {
          log.error('[ShippingSampleSelector] Forbidden loading sample by label:', error);
          showSnackbar('You are not authorized to view that sample.', 'error');
        } else if (error.status >= 500) {
          log.error('[ShippingSampleSelector] Server error loading sample by label:', error);
          showSnackbar('Something went wrong while looking up the sample. Please try again later.', 'error');
        } else {
          log.error('[ShippingSampleSelector] Failed to add sample by label:', error);
          showSnackbar('Failed to look up the sample. Please try again.', 'error');
        }
      } else {
        log.error('[ShippingSampleSelector] Failed to add sample by label:', error);
        showSnackbar('Failed to look up the sample. Please try again.', 'error');
      }
    }
  };

  const handleRemoveSample = (sampleId: number) => {
    onSamplesChange(samples.filter((s) => s.sampleId !== sampleId));
    onSamplesModified?.();
  };

  const handleLabelTypeChange = (sampleId: number, labelType: string) => {
    onSamplesChange(samples.map((s) => (s.sampleId === sampleId ? { ...s, labelType } : s)));
  };

  const shippingForTable: ShippingDto = {
    samples,
  } as ShippingDto;

  return (
    <>
      <ShippingSamplesTable
        shipping={shippingForTable}
        isLocalTransfer={isLocalTransfer}
        isPending={isPending}
        isSubmitted={isSubmitted}
        isReadOnly={isReadOnly}
        availableLabelTypes={availableLabelTypes}
        sampleLabelToAdd={sampleLabelToAdd}
        onSampleLabelChange={setSampleLabelToAdd}
        onAddSample={handleAddSampleByLabel}
        onRemoveSample={handleRemoveSample}
        onLabelTypeChange={handleLabelTypeChange}
        onShowSampleDialog={handleOpenSampleDialog}
        requiredToSubmit={requiredToSubmit}
        errorMessage={errorMessage}
      />
      <SampleSelectionDialog
        open={showSampleDialog}
        onClose={handleCloseSampleDialog}
        title={
          shippingDialogLaunchFrom === 'shippingsPage'
            ? 'Select samples'
            : `Select samples from project ${projectLabel || '(project)'}`
        }
        allowMultiple={true}
        onSamplesSelect={async (selectedRows) => {
          const toAdd: SampleWithLabelDto[] = [];
          const useGlobalLookup = shippingDialogLaunchFrom === 'shippingsPage';
          for (const row of selectedRows) {
            if (row.id && row.sampleLabel) {
              try {
                const sample = useGlobalLookup
                  ? await backend.sample.get(row.sampleLabel)
                  : await backend.project.getSample(projectLabel, row.sampleLabel);
                if (!samples.some((s) => s.sampleId === sample.id) && !toAdd.some((s) => s.sampleId === sample.id)) {
                  toAdd.push({
                    sampleId: sample.id!,
                    sample,
                    labelType: undefined,
                  });
                }
              } catch (error) {
                log.error('[ShippingSampleSelector] Failed to load sample:', error);
                showSnackbar('Failed to add sample', 'error');
              }
            }
          }
          if (toAdd.length > 0) {
            onSamplesChange([...samples, ...toAdd]);
            onSamplesModified?.();
          }
        }}
      />
    </>
  );
};
